import type { Announcement } from './types.ts';

/**
 * The catalog of What's-New announcements, newest first. This is the source
 * of truth for both the in-app feed and the marketing-site changelog digest.
 *
 * Authoring workflow: add an entry (usually in the same PR as the feature,
 * gated by its flag with `status: 'hidden'`), then flip `status` /
 * set `gaDate` in a follow-up one-line PR when the feature goes GA. Entry
 * copy is customer-facing — write from the user's side of the screen.
 *
 * Entries through 2026-07-13 are backfilled from the retired platform-release
 * changelogs (releases 0.0.1–0.8.0).
 */
export const ANNOUNCEMENTS: Announcement[] = [
  {
    key: 'leave-workspace',
    title: 'Leave a workspace',
    body: "You can now remove yourself from a workspace from Settings → Profile. Your projects, comments and history stay behind with your name on them, and reports on your team keep counting your work. An admin can invite you back at any time. A workspace always keeps at least one Owner, so if you're the only one you'll be asked to make someone else an Owner or delete the workspace first.",
    type: 'new',
    status: 'ga',
    publishedAt: '2026-09-11',
    gaDate: '2026-09-11',
    cta: { label: 'Open Profile settings', route: '/settings/profile' },
  },
  {
    key: 'privacy-policy-and-terms',
    title: 'Privacy Policy & Terms of Service',
    body: 'We published our Privacy Policy and Terms of Service, linked from every page footer on [salestrak.io](https://salestrak.io).',
    type: 'improved',
    status: 'ga',
    publishedAt: '2026-07-17',
    gaDate: '2026-07-17',
  },
  {
    key: 'widgets',
    title: 'Widget dashboards',
    body: 'Build custom dashboards from a library of widgets — pipeline, leaderboards, territory density, and more — on My Work and every workflow and team.',
    type: 'new',
    status: 'ga',
    publishedAt: '2026-07-15',
    gaDate: '2026-09-18',
    cta: { label: 'Open My Work', route: '/my-work' },
  },
  {
    key: 'redesigned-navigation',
    title: 'A redesigned navigation',
    body: 'A completely new navigation system: the Module Rail puts each area of SalesTrak one click away, with module-specific menus and a reorganized header. Reorder and show/hide your rail tiles — your setup syncs across all your devices.',
    type: 'new',
    status: 'ga',
    publishedAt: '2026-07-13',
    gaDate: '2026-07-13',
  },
  {
    key: 'roles-and-permissions',
    title: 'Roles & permissions',
    body: 'Granular control over who can see and do what in your workspace — roles, per-area permissions, and private workflows and items.',
    type: 'new',
    status: 'ga',
    publishedAt: '2026-07-13',
    gaDate: '2026-07-13',
  },
  {
    key: 'richer-notes',
    title: 'A richer notes editor',
    body: 'Notes now support @mentions for members and teams, slash commands for quick actions, and drag-and-drop reordering of content. Unread notes are marked read automatically as you view them.',
    type: 'new',
    status: 'ga',
    publishedAt: '2026-07-13',
    gaDate: '2026-07-13',
  },
  {
    key: 'issue-sources',
    title: 'Sources for projects',
    body: 'Track where every project originates with the new Sources feature — existing Lead Source data migrates automatically. Assignment is simpler too: owners and assignees are now one assignees list, with the primary assignee receiving notifications.',
    type: 'new',
    status: 'ga',
    publishedAt: '2026-07-13',
    gaDate: '2026-07-13',
  },
  {
    key: 'custom-fields-and-tables',
    title: 'Custom fields & tables, upgraded',
    body: 'Select and Member fields can allow multiple selections, a Checkbox field type joins the lineup, and fields gained descriptions and default values. Tables now support column resizing and reordering that persist per member.',
    type: 'improved',
    status: 'ga',
    publishedAt: '2026-07-13',
    gaDate: '2026-07-13',
  },
  {
    key: 'photos-on-issues',
    title: 'Photos on projects',
    body: 'Attach and reorder photos directly on project fields — roof damage documentation stays with the project. You can also drop images, PDFs, videos, and documents straight into notes.',
    type: 'new',
    status: 'early-access',
    publishedAt: '2026-07-10',
    flagKey: 'PHOTOS',
  },
  {
    key: 'maps-remember-view',
    title: 'Maps remember where you left off',
    body: 'The map now restores your last viewed location and zoom level per workspace when you refresh or navigate back — no more snapping to your GPS position. Map filters got fixes too.',
    type: 'improved',
    status: 'ga',
    publishedAt: '2026-06-03',
    gaDate: '2026-06-03',
  },
  {
    key: 'marketing-site',
    title: 'New salestrak.io',
    body: "Our new marketing site is live at [salestrak.io](https://salestrak.io), including this public What's-New changelog.",
    type: 'new',
    status: 'ga',
    publishedAt: '2026-05-27',
    gaDate: '2026-05-27',
  },
  {
    key: 'interface-polish',
    title: 'A cleaner, more consistent interface',
    body: 'Page headers got a cleaner layout with a modern toolbar. Custom field cells now support copy, clear, edit, and one-tap phone/email actions with validation feedback. Team, Reasons, and Workflow settings were polished throughout.',
    type: 'improved',
    status: 'ga',
    publishedAt: '2026-05-20',
    gaDate: '2026-05-20',
  },
  {
    key: 'may-under-the-hood',
    title: 'Under the hood',
    body: 'Dialogs no longer close unexpectedly when using menus inside them, side panels close on navigation, map pins navigate to existing buildings instead of creating duplicates, and fullscreen dialogs size correctly on iPad.',
    type: 'under-the-hood',
    status: 'ga',
    publishedAt: '2026-05-14',
    gaDate: '2026-05-14',
  },
  {
    key: 'mobile-experience',
    title: 'A better SalesTrak on your phone',
    body: 'Menus and popovers become native-feeling sheets on small screens, the Kanban board is fully responsive, global search is easier to reach, and a new collapsible sidebar frees up space when you need it.',
    type: 'new',
    status: 'ga',
    publishedAt: '2026-05-11',
    gaDate: '2026-05-11',
  },
  {
    key: 'faster-team-invites',
    title: 'Invite your team faster',
    body: 'Paste a whole list of email addresses and each becomes an invite row automatically. Assign teams right in the invitation dialog, with sensible role defaults for new members.',
    type: 'improved',
    status: 'ga',
    publishedAt: '2026-05-11',
    gaDate: '2026-05-11',
  },
  {
    key: 'invitation-management',
    title: 'Teammate invitations, cleaned up',
    body: 'Cancel pending invitations, see invited members clearly marked in selectors, and get a clear message when an invite link is no longer valid. Profiles gained a status badge and a directly accessible Danger Zone.',
    type: 'improved',
    status: 'ga',
    publishedAt: '2026-04-22',
    gaDate: '2026-04-22',
  },
  {
    key: 'map-accuracy',
    title: 'Sharper map location accuracy',
    body: 'GPS accuracy improved significantly, with an optional device-sensors setting for even better precision offline. The map keeps your last position across visits, and the app now updates itself automatically when a new version ships.',
    type: 'improved',
    status: 'ga',
    publishedAt: '2026-04-16',
    gaDate: '2026-04-16',
  },
  {
    key: 'dashboard-overview',
    title: 'Dashboard overview',
    body: 'A new Dashboard with an overview screen, drilldowns, and stage breakdown tables. "Only me" filtering also works consistently across List, Board, and Map views.',
    type: 'new',
    status: 'ga',
    publishedAt: '2026-04-04',
    gaDate: '2026-04-04',
  },
];
